import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';

function formatJoined(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return '—';
  }
}

function formatDurationShort(totalSeconds) {
  const sec = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (sec <= 0) return '0 мин';
  const mins = Math.max(1, Math.round(sec / 60));
  if (mins < 60) return `${mins} мин`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hours} ч ${rem} мин` : `${hours} ч`;
}

function formatVisitSummary(row) {
  const count = Number(row.visit_count) || 0;
  const when = row.last_visit_at || row.last_login;
  const duration = Number(row.total_duration_seconds) || 0;
  if (count === 0 && !when) {
    return 'пока нет данных';
  }
  const parts = [];
  if (count > 0) {
    parts.push(`${count} ${count === 1 ? 'раз' : count < 5 ? 'раза' : 'раз'}`);
  } else if (when) {
    parts.push('вход');
  }
  if (when) {
    parts.push(formatJoined(when));
  }
  if (duration > 0) {
    parts.push(formatDurationShort(duration));
  }
  return parts.join(' · ');
}

export default function TeacherNewUsersPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('unread');
  const [rows, setRows] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const pageSize = 30;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('filter', filter);
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      const res = await api.get(`/teacher/new-users/?${params.toString()}`);
      const data = res.data;
      const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setRows(list);
      setUnreadCount(data?.unread_count ?? 0);
      setTotalCount(data?.count ?? list.length);
    } catch (e) {
      setError(
        e.response?.status === 403
          ? 'Раздел доступен только администратору сайта.'
          : e.response?.data?.detail || 'Не удалось загрузить список.',
      );
      setRows([]);
      setUnreadCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  async function markRead(userId, isRead) {
    setBusyId(userId);
    setError('');
    try {
      if (isRead) {
        await api.post(`/teacher/new-users/${userId}/read/`);
      } else {
        await api.delete(`/teacher/new-users/${userId}/read/`);
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось обновить отметку.');
    } finally {
      setBusyId(null);
    }
  }

  async function invite(userId) {
    setBusyId(userId);
    setError('');
    try {
      await api.post(`/teacher/new-users/${userId}/invite/`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось отправить приглашение.');
    } finally {
      setBusyId(null);
    }
  }

  async function openQuestionnaire(userId) {
    setBusyId(userId);
    try {
      await api.post(`/teacher/new-users/${userId}/read/`).catch(() => {});
      navigate(`/teacher/students/${userId}/questionnaire`, {
        state: { from: 'new-users' },
      });
    } finally {
      setBusyId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="teacher-new-users-page">
      <div className="page-header">
        <p>
          <Link to="/teacher" className="back-link">← Кабинет преподавателя</Link>
        </p>
        <h1>Новые пользователи</h1>
        <p className="page-subtitle">
          Последние регистрации на сайте. Можно посмотреть анкету, активность и сразу добавить в ученики.
          Учёт времени на сайте — с момента включения этой функции.
        </p>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Все
        </button>
        <button
          type="button"
          className={`tab-btn ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Непрочитанные{unreadCount > 0 ? ` (${unreadCount})` : ''}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-screen">
          <div className="spinner" />
          <p>Загрузка...</p>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-secondary">
          {filter === 'unread' ? 'Нет непрочитанных регистраций.' : 'Пока нет регистраций.'}
        </p>
      ) : (
        <>
          <table className="table teacher-new-users-table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Имя</th>
                <th>Контакт</th>
                <th>Анкета</th>
                <th>На сайте</th>
                <th>Ученик</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const busy = busyId === row.id;
                return (
                  <tr key={row.id} className={row.is_read ? '' : 'teacher-new-users-row--unread'}>
                    <td>{formatJoined(row.date_joined)}</td>
                    <td>
                      <strong>{row.display_name || row.first_name || row.username}</strong>
                      <div className="text-secondary">@{row.username}</div>
                    </td>
                    <td>
                      <span title={row.email || ''}>{row.contact_display || '—'}</span>
                    </td>
                    <td>{row.has_questionnaire ? 'Есть' : 'Нет'}</td>
                    <td className="teacher-new-users-visit-cell">
                      {formatVisitSummary(row)}
                    </td>
                    <td>
                      {row.is_my_student ? (
                        <Link to={`/teacher/students/${row.id}`}>Да</Link>
                      ) : (
                        'Нет'
                      )}
                    </td>
                    <td>{row.is_read ? 'Прочитано' : 'Новое'}</td>
                    <td>
                      <div className="teacher-new-users-actions">
                        <Link
                          to={`/teacher/new-users/${row.id}/activity`}
                          className="btn btn-secondary btn-sm"
                        >
                          Активность
                        </Link>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={busy}
                          onClick={() => openQuestionnaire(row.id)}
                        >
                          Анкета
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          disabled={busy || row.is_my_student}
                          onClick={() => invite(row.id)}
                          title={row.is_my_student ? 'Уже ваш ученик' : 'Добавить в ученики'}
                        >
                          {row.is_my_student ? 'Уже ученик' : 'Пригласить'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={busy}
                          onClick={() => markRead(row.id, !row.is_read)}
                        >
                          {row.is_read ? 'Непрочитано' : 'Прочитано'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination-bar">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Назад
              </button>
              <span className="text-secondary">
                Стр. {page} из {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Вперёд
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
